/**
 * Recovery Scenarios Section
 * 
 * Shows practical recovery scenarios — what happens if you lose your device,
 * break a tag, or need to recover from scratch. Reframed from "security levels"
 * to avoid implying the device isn't secure with fewer tags.
 * 
 * Key changes from previous version:
 * - Renamed from "Your Security, Your Way" to "Recovery Scenarios"
 * - Removed "Security 1/3" meter that implied incomplete security
 * - Removed confusing "Tags + Phone + Contacts" graphic
 * - Trusted contacts available at all levels (not just 3 tags)
 * - Scenarios focus on practical recovery situations, not security grades
 */

import { useState } from 'react';
import { RefreshCw, MapPin, ShieldCheck, ChevronRight, Smartphone, Tag } from 'lucide-react';

interface RecoveryScenario {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  howItWorks: string[];
  icon: typeof RefreshCw;
  color: string;
  bgGradient: string;
  illustration: React.ReactNode;
}

/* ── Inline SVG illustrations for each scenario ── */

function LostDeviceIllustration({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Device with question mark */}
      <rect x="60" y="20" width="80" height="120" rx="12" fill="rgba(0,0,0,0.03)" stroke="rgba(0,0,0,0.1)" strokeWidth="1.5" />
      <text x="100" y="90" textAnchor="middle" fontSize="36" fill="rgba(0,0,0,0.12)" fontWeight="bold">?</text>
      {/* Recovery tag with checkmark */}
      <g className="animate-pulse" style={{ animationDuration: '3s' }}>
        <rect x="140" y="100" width="50" height="35" rx="6" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
        <path d="M155 118 L162 125 L175 112" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {/* Arrow from tag to device */}
      <path d="M140 117 L128 100" stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
    </svg>
  );
}

function BrokenTagIllustration({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Broken tag */}
      <g opacity="0.3">
        <rect x="30" y="55" width="50" height="35" rx="6" fill="rgba(0,0,0,0.03)" stroke="rgba(0,0,0,0.15)" strokeWidth="1.5" strokeDasharray="4 3" />
        <line x1="40" y1="60" x2="70" y2="85" stroke="rgba(0,0,0,0.1)" strokeWidth="1.5" />
      </g>
      {/* Backup tag 1 - healthy */}
      <g className="animate-pulse" style={{ animationDuration: '2.5s' }}>
        <rect x="100" y="35" width="50" height="35" rx="6" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
        <path d="M115 53 L122 60 L140 47" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {/* Backup tag 2 - healthy */}
      <g className="animate-pulse" style={{ animationDuration: '3.5s' }}>
        <rect x="100" y="85" width="50" height="35" rx="6" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
        <path d="M115 103 L122 110 L140 97" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {/* Arrow */}
      <path d="M80 72 L100 55" stroke="rgba(0,0,0,0.08)" strokeWidth="1" strokeDasharray="4 3" />
      <path d="M80 72 L100 100" stroke="rgba(0,0,0,0.08)" strokeWidth="1" strokeDasharray="4 3" />
    </svg>
  );
}

function TrustedContactIllustration({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Central user */}
      <circle cx="100" cy="65" r="18" fill="rgba(0,0,0,0.03)" stroke="rgba(0,0,0,0.12)" strokeWidth="1.5" />
      <circle cx="100" cy="58" r="6" fill="rgba(0,0,0,0.08)" />
      <path d="M88 72 Q100 82 112 72" stroke="rgba(0,0,0,0.08)" strokeWidth="1.5" fill="none" />
      {/* Trusted contact 1 */}
      <g className="animate-pulse" style={{ animationDuration: '3s' }}>
        <circle cx="50" cy="110" r="14" fill={`${color}10`} stroke={color} strokeWidth="1.5" />
        <circle cx="50" cy="105" r="4.5" fill={`${color}30`} />
        <path d="M41 115 Q50 122 59 115" stroke={`${color}50`} strokeWidth="1.5" fill="none" />
      </g>
      {/* Trusted contact 2 */}
      <g className="animate-pulse" style={{ animationDuration: '4s' }}>
        <circle cx="150" cy="110" r="14" fill={`${color}10`} stroke={color} strokeWidth="1.5" />
        <circle cx="150" cy="105" r="4.5" fill={`${color}30`} />
        <path d="M141 115 Q150 122 159 115" stroke={`${color}50`} strokeWidth="1.5" fill="none" />
      </g>
      {/* Connection lines */}
      <line x1="85" y1="78" x2="60" y2="98" stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.3" />
      <line x1="115" y1="78" x2="140" y2="98" stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.3" />
      {/* Shield icon at center bottom */}
      <g transform="translate(92, 120)">
        <path d="M8 0 L16 4 L16 10 Q16 16 8 20 Q0 16 0 10 L0 4 Z" fill={`${color}20`} stroke={color} strokeWidth="1" />
        <path d="M5 10 L7.5 12.5 L12 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

const scenarios: RecoveryScenario[] = [
  {
    id: 'lost-device',
    title: 'Lost or Stolen Device',
    subtitle: 'Recover with any Recovery Tag',
    description: 'Your Ryder One is lost or stolen? No problem. Tap any Recovery Tag to a new device and your wallet is restored instantly. Your assets stay safe because the device is protected by your passcode.',
    howItWorks: [
      'Get a replacement Ryder One',
      'Tap your Recovery Tag to the new device',
      'Enter your passcode — wallet restored',
      'Old device is useless without your passcode',
    ],
    icon: RefreshCw,
    color: '#22c55e',
    bgGradient: 'from-green-500/10 to-green-500/5',
    illustration: <LostDeviceIllustration color="#22c55e" />,
  },
  {
    id: 'broken-tag',
    title: 'Damaged Recovery Tag',
    subtitle: 'Redundancy keeps you safe',
    description: 'If a Recovery Tag gets damaged or lost, your other tags still work independently. Each tag, combined with your paired phone, gives you full recovery — you only need one working tag plus your phone to restore everything.',
    howItWorks: [
      'Each Recovery Tag + your paired phone = full recovery',
      'Store tags in different locations for redundancy',
      'One working tag + your paired phone is all you need',
      'Order replacement tags anytime from the Ryder app',
    ],
    icon: MapPin,
    color: '#6a63f5',
    bgGradient: 'from-indigo-500/10 to-indigo-500/5',
    illustration: <BrokenTagIllustration color="#6a63f5" />,
  },
  {
    id: 'trusted-contacts',
    title: 'Recovery contact',
    subtitle: 'Fully self-custodial social recovery',
    description: 'Designate recovery contacts who can help you regain access if you lose both your device and all your tags. Fully self-custodial — your contacts never have access to your keys or funds.',
    howItWorks: [
      'Set up recovery contacts in the Ryder app',
      'Contacts hold encrypted recovery fragments on their own devices',
      'No single contact can access your wallet — fully self-custodial',
      'Combine fragments to restore full access, no third party involved',
    ],
    icon: ShieldCheck,
    color: '#f59e0b',
    bgGradient: 'from-amber-500/10 to-amber-500/5',
    illustration: <TrustedContactIllustration color="#f59e0b" />,
  },
];

export default function TapSafeSection() {
  const [activeScenario, setActiveScenario] = useState(0);
  const current = scenarios[activeScenario];
  const Icon = current.icon;

  return (
    <section id="security" className="py-16 sm:py-24 overflow-hidden" style={{ backgroundColor: 'white' }}>
      <div className="container px-4 max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#22c55e]/5 border border-[#22c55e]/10 rounded-full px-4 py-1.5 mb-6">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
            <span className="text-xs font-medium text-green-700/70 uppercase tracking-wider">Always Recoverable</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 tracking-tight text-gray-900">
            Recovery scenarios
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            No matter what happens — lost device, damaged tag, or worst-case scenario — you always have a path back to your crypto.
          </p>
        </div>

        {/* Scenario Tabs */}
        <div className="max-w-5xl mx-auto">
          {/* Card Selector — matches "You're Never Locked In" design */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
            {scenarios.map((scenario, index) => {
              const isActive = activeScenario === index;
              const TabIcon = scenario.icon;
              return (
                <button
                  key={scenario.id}
                  onClick={() => setActiveScenario(index)}
                  className={`
                    relative text-left p-5 sm:p-6 rounded-2xl transition-all duration-300 border
                    ${isActive
                      ? 'bg-white border-gray-200 shadow-lg shadow-gray-200/50'
                      : 'bg-white/50 border-gray-100 hover:bg-white hover:border-gray-200 hover:shadow-md'
                    }
                  `}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${scenario.color}${isActive ? '20' : '10'}` }}
                  >
                    <TabIcon
                      className="w-5 h-5 transition-colors duration-300"
                      style={{ color: isActive ? scenario.color : 'rgba(0,0,0,0.3)' }}
                    />
                  </div>
                  <h3 className={`text-base font-semibold mb-1 transition-colors ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                    {scenario.title}
                  </h3>
                  <p className="text-sm text-gray-400 line-clamp-2">
                    {scenario.subtitle}
                  </p>
                  {isActive && (
                    <div
                      className="absolute bottom-0 left-6 right-6 h-0.5 rounded-full"
                      style={{ backgroundColor: scenario.color }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Scenario Detail Card */}
          <div
            className="relative rounded-3xl border border-gray-200 overflow-hidden transition-all duration-500 bg-white"
            style={{ boxShadow: `0 0 60px ${current.color}08` }}
          >
            {/* Background gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${current.bgGradient} opacity-50`} />

            <div className="relative p-6 sm:p-10">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                {/* Left: Scenario Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${current.color}15` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: current.color }} />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                        {current.title}
                      </h3>
                      <p className="text-sm text-gray-400">{current.subtitle}</p>
                    </div>
                  </div>

                  <p className="text-gray-500 mb-6 text-base leading-relaxed">
                    {current.description}
                  </p>

                  {/* How it works steps */}
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">How it works</p>
                    {current.howItWorks.map((step, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5"
                          style={{ backgroundColor: `${current.color}20`, color: current.color }}
                        >
                          {i + 1}
                        </div>
                        <span className="text-sm text-gray-600">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: SVG Illustration */}
                <div className="lg:w-64 flex items-center justify-center">
                  <div className="w-48 h-40 sm:w-56 sm:h-44">
                    {current.illustration}
                  </div>
                </div>
              </div>

              {/* Bottom bar */}
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    <span>1 Recovery Tag included</span>
                  </div>
                  <span className="text-gray-200">|</span>
                  <div className="flex items-center gap-1.5">
                    <Smartphone className="w-3.5 h-3.5" />
                    <span>Manage via Ryder app</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const heroSection = document.getElementById('hero');
                    if (heroSection) {
                      heroSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="flex items-center gap-2 text-sm font-semibold px-6 py-2.5 rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap bg-gray-900 text-white"
                  style={{
                    boxShadow: `0 4px 16px rgba(0,0,0,0.15)`
                  }}
                >
                  Get Ryder One
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
